import React, { useState } from 'react'
import styled from 'styled-components'
import moment, { Moment } from 'moment'
import { tint, transparentize } from 'polished'

import { formatPrice } from '../../utils/pricing'
import { findAvailability } from './dayAvailability'

import { DiscountMarker } from './discounts'
import { DaysAvailabilities, FocusedInput } from './types'

const DayNumber = styled.div`
  color: #333;
  font-size: 16px;
`

const Detail = styled.div`
  height: 12px;
  font-size: 11px;
  margin-bottom: 4px;
  line-height: 1;
  color: ${tint(0.4, '#333')};
  overflow: visible;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  text-align: center;
  align-items: center;
  padding: 4px 0;
  font-weight: 400;

  box-sizing: border-box;
  width: 100%;
  height: 100%;

  // Ensure that focus/active outlines render correctly even when this element
  // has a background set:
  outline: inherit;

  &.discount {
    ${DiscountMarker} {
      position: absolute;
      top: 4px;
      left: 4px;
    }

    ${Detail} {
      color: ${({ theme }) => theme?.['-ds-core-400']?.value};
    }
  }

  &.check-out-only {
    background: linear-gradient(
      to bottom right,
      #fff 0%,
      #fff 49.9%,
      #ececec 50%,
      #ececec 100%
    );

    .CalendarDay__hovered_span & {
      background: none;
    }
  }

  &.check-in-only {
    background: linear-gradient(
      to top left,
      #fff 0%,
      #fff 49.9%,
      #ececec 50%,
      #ececec 100%
    );
  }

  .CalendarDay__blocked_out_of_range:not(.CalendarDay__blocked_calendar) &:not(.past),
  .CalendarDay__blocked_minimum_nights &,
  &.check-out-only {
    cursor: pointer;
  }

  &.check-out-only,
  &.check-in-only {
    .CalendarDay:not(.CalendarDay__blocked_calendar):not(.CalendarDay__blocked_out_of_range)
      &:hover,
    .CalendarDay__selected & {
      background: none;
    }
  }

  &.past, &.after-max {
    .CalendarDay__blocked_out_of_range:not(.CalendarDay__blocked_calendar):not(.CalendarDay__selected_span):not(.CalendarDay__selected_end) & {
      background: #fff;
    }
  }

  &.past {
    .CalendarDay__blocked_out_of_range & {
      ${Detail} {
        // Hide pricing from user but still have it impact the layout:
        visibility: hidden;
      }
    }
  }

  .CalendarDay__default:active &,
  .CalendarDay__default:hover & {
    ${DayNumber}, ${Detail} {
      color: #333;
    }
  }

  .CalendarDay__blocked_calendar &,
  .CalendarDay__blocked_calendar:active &,
  .CalendarDay__blocked_calendar:hover & {
    ${DayNumber}, ${Detail} {
      color: ${tint(0.4, '#333')};
    }
  }

  .CalendarDay__blocked_out_of_range &,
  .CalendarDay__blocked_out_of_range:active &,
  .CalendarDay__blocked_out_of_range:hover &,
  .CalendarDay__blocked_minimum_nights & {
    ${DayNumber}, ${Detail} {
      color: ${tint(0.7, '#333')};
    }
  }

  .CalendarDay__selected_span &,
  .CalendarDay__selected_span:active &,
  .CalendarDay__selected_span:hover &,
  .CalendarDay__hovered_span &:not(.check-in-only) {
    ${DayNumber}, ${Detail} {
      color: #333;
    }
  }

  .CalendarDay__selected &,
  .CalendarDay__selected:active &,
  .CalendarDay__selected:hover & {
    ${DiscountMarker} {
      background-color: ${transparentize(0.6, '#fff')};
    }

    ${DayNumber} {
      color: #fff;
    }

    ${Detail} {
      color: ${transparentize(0.6, '#fff')};
    }
  }

  /* .CalendarDay__blocked_out_of_range &.past,
  .CalendarDay__blocked_calendar &:not(.check-out-only) {
    ${DayNumber} {
      text-decoration: line-through;
    }
  } */
`

type CalendarDayProps = {
  focusedInput: FocusedInput
  day: Moment
  daysData: DaysAvailabilities
  today: Moment
  maxStartDate?: Moment
  startDate?: Moment
  firstUnavailableDate?: Moment
  activeDiscount?: DaysAvailabilities[string]['discount']
  onTooltipOpen: (referenceElement: HTMLElement, message: string) => unknown
}

const CalendarDay = ({
  focusedInput,
  day,
  daysData,
  today,
  maxStartDate,
  startDate,
  firstUnavailableDate,
  activeDiscount,
  onTooltipOpen,
}: CalendarDayProps) => {
  const [dayNumberElement, setDayNumberElement] = useState<HTMLElement>(null)

  const inThePast = day.isBefore(today)
  const afterMaxStart = day.isAfter(maxStartDate)

  const dayData = findAvailability({ day, daysData })
  const isAvailable = dayData?.available !== false

  const previousDayData = findAvailability({
    day: moment(day).subtract(1, 'day'),
    daysData,
  })
  const previousDayAvailable = previousDayData?.available !== false
  const availableForCheckInOnly = isAvailable && !previousDayAvailable
  const availableForCheckOutOnly = !isAvailable && previousDayAvailable
  const beyondFirstUnavailable =
    firstUnavailableDate && day.isAfter(firstUnavailableDate)

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const parentClassName = e.currentTarget.parentElement.className
    const isBlocked = parentClassName.includes('CalendarDay__blocked')
    const isUnavailable =
      parentClassName.includes('CalendarDay__blocked_calendar') &&
      !availableForCheckOutOnly
    const beforeStartDate =
      startDate && day.isBefore(moment(startDate).startOf('day'))

    // No tooltip for dates that aren't blocked, are in the past, or are
    // clearly unavailable (due to being booked):
    if (isUnavailable || !isBlocked || inThePast) {
      return
    }

    let message: string
    if (focusedInput === 'startDate' || beforeStartDate) {
      if (afterMaxStart) {
        const latestCheckIn = maxStartDate.format('D MMM YYYY')
        message = `Check-in cannot be later than ${latestCheckIn}`
      } else if (availableForCheckOutOnly) {
        message = 'Checkout only'
      } else if (dayData.minimumNights > 1) {
        message = `${dayData.minimumNights} night minimum stay`
      }
    } else if (parentClassName.includes('minimum_nights')) {
      const startDayData = findAvailability({ day: startDate, daysData })
      if (startDayData.minimumNights > 1) {
        // When selecting an end date some days are blocked because they don't
        // meet the minimum nights:
        message = `${startDayData.minimumNights} night minimum stay`
      }
    } else if (beyondFirstUnavailable) {
      const latestCheckOut = firstUnavailableDate.format('D MMM YYYY')
      message = `Closest available checkout is ${latestCheckOut}`
    }

    if (message) {
      onTooltipOpen(dayNumberElement, message)
    }
  }

  const inSelectableRange = isAvailable && !inThePast && !beyondFirstUnavailable
  // Only show discount if one exists, if the day is selectable and if the
  // discount is the same as the discount of the selected start date (if one is
  // selected):
  const showDiscount =
    inSelectableRange &&
    dayData?.discount &&
    (!startDate || activeDiscount?.id === dayData.discount.id)

  return (
    <Wrapper
      className={[
        inThePast && 'past',
        afterMaxStart && 'after-max',
        availableForCheckInOnly && 'check-in-only',
        availableForCheckOutOnly && 'check-out-only',
        showDiscount && 'discount',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {showDiscount && <DiscountMarker />}
      <DayNumber ref={setDayNumberElement}>{day.format('D')}</DayNumber>
      <Detail>{isAvailable && dayData && formatPrice(dayData.price)}</Detail>
    </Wrapper>
  )
}

export default React.memo(CalendarDay)
